import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import React, { useRef, useEffect, FC, RefObject, ReactNode } from "react";
import ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { zIndex } from "../../../constants";
import { useThemeColors } from "../../../controllers";
import { useClickOutsideEl, useKeyPress } from "../../../utils/hooks";
import LockBodyScroll from "../../atoms/LockBodyScroll";
import { Outer, Inner, Popup, Close, CloseButton } from "./styled";

interface RequiredProps {
    onClose: () => void;
}

type Props = RequiredProps & {
    children?: ReactNode;
    maxWidth?: number | string;
    alignTopOnSmall?: boolean;
    isVisible?: boolean;
    hasCloseButton?: boolean;
    horizontalPadding?: number;
    closeButtonColor?: string;
};

const duration = 300;

const TransitionWrapper = styled.div`
    position: relative;
    z-index: ${zIndex.modal};

    &.modal-enter {
        ${Outer} {
            opacity: 0;
        }

        ${Popup} {
            transform: translateY(20px);
        }
    }

    &.modal-enter-done,
    &.modal-enter-active {
        ${Outer} {
            opacity: 1;
            transition: ease opacity ${duration}ms;
        }

        ${Popup} {
            transition: ease transform ${duration}ms;
            transform: translateY(0);
        }
    }

    &.modal-exit {
        ${Outer} {
            opacity: 1;
        }
        ${Popup} {
            transform: translateY(0);
        }
    }
    &.modal-exit-done,
    &.modal-exit-active {
        ${Outer} {
            opacity: 0.01;
            transition: ease opacity ${duration}ms;
        }

        ${Popup} {
            transition: ease transform ${duration}ms;
            transform: translateY(20px);
        }
    }
`;

// Provides an onClose method that ensures only the top most modal is closed at a time
const modalStack: string[] = [];
export const CloseHandler: FC<{
    onClose: () => void;
    popupRef: RefObject<HTMLDivElement>;
    children: (onClose: () => void) => ReactNode;
}> = props => {
    const id = useRef(uuid());

    useEffect(() => {
        modalStack.push(id.current);
    }, []);

    const handleClose = () => {
        if (modalStack[modalStack.length - 1] === id.current) {
            props.onClose();
            modalStack.pop();
        }
    };

    useClickOutsideEl(props.popupRef, handleClose);

    useKeyPress("Escape", handleClose);

    return <>{props.children(handleClose)}</>;
};

export const Modal: FC<Props> = props => {
    const portalRef = useRef<HTMLElement>(document.createElement("div"));
    const popupRef = useRef<HTMLDivElement>(null);
    const scrollingRef = useRef<HTMLDivElement>(null);
    const { isVisible } = props;
    const { setThemeColor } = useThemeColors();

    // Creates the portal node
    useEffect(() => {
        const el = portalRef.current;
        const body = document.body;
        body.insertAdjacentElement("beforeend", el);
        return () => {
            el.parentNode?.removeChild(el);
        };
    }, []);

    useEffect(() => {
        if (isVisible) {
            setThemeColor(colors => colors.modal);
        } else {
            setThemeColor(colors => colors.default);
        }
    }, [isVisible, setThemeColor]);

    return ReactDOM.createPortal(
        <CSSTransition
            timeout={duration}
            classNames="modal"
            in={props.isVisible}
            unmountOnExit
            mountOnEnter
        >
            <TransitionWrapper>
                <CloseHandler onClose={props.onClose} popupRef={popupRef}>
                    {handleClose => (
                        <Outer ref={scrollingRef}>
                            <LockBodyScroll scrollingRef={scrollingRef} />
                            <Inner
                                alignTopOnSmall={props.alignTopOnSmall}
                                horizontalPadding={props.horizontalPadding}
                            >
                                <Popup
                                    style={{ maxWidth: props.maxWidth }}
                                    ref={popupRef as RefObject<HTMLDivElement>}
                                >
                                    {props.hasCloseButton && (
                                        <Close>
                                            <CloseButton onClick={handleClose} color={props.closeButtonColor}>
                                                <CloseRoundedIcon />
                                            </CloseButton>
                                        </Close>
                                    )}
                                    {props.children}
                                </Popup>
                            </Inner>
                        </Outer>
                    )}
                </CloseHandler>
            </TransitionWrapper>
        </CSSTransition>,
        portalRef.current,
    );
};

export const SmallModal: FC<Partial<Props> & RequiredProps> = props => {
    return <Modal hasCloseButton maxWidth={350} horizontalPadding={20} {...props} />;
};
